/**
 * Pure edits on line-edge segments (door / window / opening / alcove entries
 * on any host: primary edge, alcove sub-wall, chamfer diagonal). All clamp
 * internally against the host's resolved span and the segment's neighbors.
 */
import { clamp, lineSegPositionRange, lineSegWidthRange, roundToHalfInch } from '../constraints';
import { type LineEdgeRef, resolveLineRef } from '../refs';
import { type DoorSwing, type EdgeSegmentType, type FloorPlanEdgeSegment, type FloorPlanRoom, roomCeilingHeight, segmentOpeningBase } from '../schemas';
import { edgeItemWithDefaults } from './constructors';
import { updateHostSegment, updateHostSegments } from './structural';

/** Slide a segment along its host line (width fixed), clamped between neighbors. */
export function setSegmentPosition(room: FloorPlanRoom, ref: LineEdgeRef, si: number, position: number): FloorPlanRoom {
  const line = resolveLineRef(room, ref);
  if (!line?.segments[si]) return room;
  const r = lineSegPositionRange(line.segments, si, line.lf.length, line.minBound, line.maxBound);
  return updateHostSegment(room, ref, si, seg => {
    const p = clamp(roundToHalfInch(position), r.min, r.max);
    return p === seg.position ? seg : { ...seg, position: p };
  });
}

/** Resize a segment (start fixed), clamped to its width range. */
export function setSegmentWidth(room: FloorPlanRoom, ref: LineEdgeRef, si: number, width: number): FloorPlanRoom {
  const line = resolveLineRef(room, ref);
  if (!line?.segments[si]) return room;
  const r = lineSegWidthRange(line.segments, si, line.lf.length, line.maxBound);
  return updateHostSegment(room, ref, si, seg => {
    const w = clamp(roundToHalfInch(width), r.min, r.max);
    return w === seg.width ? seg : { ...seg, width: w };
  });
}

/** Move the segment's START (end fixed): `fromStart` is the distance from the host span's start. */
export function setSegmentStart(room: FloorPlanRoom, ref: LineEdgeRef, si: number, fromStart: number): FloorPlanRoom {
  const line = resolveLineRef(room, ref);
  if (!line?.segments[si]) return room;
  const posR = lineSegPositionRange(line.segments, si, line.lf.length, line.minBound, line.maxBound);
  const minW = lineSegWidthRange(line.segments, si, line.lf.length, line.maxBound).min;
  return updateHostSegment(room, ref, si, seg => {
    const oldEnd = seg.position + seg.width;
    const position = clamp(roundToHalfInch(line.minBound + fromStart), posR.min, oldEnd - minW);
    const width = oldEnd - position;
    return position === seg.position && width === seg.width ? seg : { ...seg, position, width };
  });
}

/** Slide a segment (width fixed) so its start sits `gap` from the host span's start. */
export function setSegmentStartGap(room: FloorPlanRoom, ref: LineEdgeRef, si: number, gap: number): FloorPlanRoom {
  const line = resolveLineRef(room, ref);
  return line ? setSegmentPosition(room, ref, si, line.minBound + gap) : room;
}

/** Slide a segment (width fixed) so its end sits `gap` from the host span's end. */
export function setSegmentEndGap(room: FloorPlanRoom, ref: LineEdgeRef, si: number, gap: number): FloorPlanRoom {
  const line = resolveLineRef(room, ref);
  const seg = line?.segments[si];
  return line && seg ? setSegmentPosition(room, ref, si, line.maxBound - gap - seg.width) : room;
}

/**
 * Change a segment's kind. Leaving the alcove kind zeroes the depth and drops
 * the outer chamfers; the new kind's defaults apply non-forced (existing
 * values win) - mirroring the panel's kind select.
 */
export function setSegmentType(room: FloorPlanRoom, ref: LineEdgeRef, si: number, kind: EdgeSegmentType): FloorPlanRoom {
  return updateHostSegment(room, ref, si, seg => {
    let next = edgeItemWithDefaults(seg, kind);
    if (kind !== 'alcove') {
      next = { ...next, depth: 0, backLeftCorner: undefined, backRightCorner: undefined };
    }
    return next;
  });
}

/** Set a door's swing. */
export function setSegmentSwing(room: FloorPlanRoom, ref: LineEdgeRef, si: number, swing: DoorSwing): FloorPlanRoom {
  return updateHostSegment(room, ref, si, seg => (seg.swing === swing ? seg : { ...seg, swing }));
}

/**
 * Set (or clear, with null) a segment's opening height. A value that reaches
 * the ceiling (base + height ≥ ceiling) reverts to the tied-to-ceiling
 * encoding (openingHeight undefined) instead of clamping to an explicit
 * number - the UX path back to "up to ceiling".
 */
export function setSegmentOpeningHeight(room: FloorPlanRoom, ref: LineEdgeRef, si: number, height: number | null): FloorPlanRoom {
  return updateHostSegment(room, ref, si, seg => {
    const ceiling = roomCeilingHeight(room);
    if (height != null) height = roundToHalfInch(height);
    if (height != null && ceiling > 0 && segmentOpeningBase(seg) + height >= ceiling - 1e-9) height = null;
    if (height == null) return seg.openingHeight === undefined ? seg : { ...seg, openingHeight: undefined };
    const h = Math.max(0, height);
    return h === seg.openingHeight ? seg : { ...seg, openingHeight: h };
  });
}

/** Set a segment's opening base (sill) height, ≥ 0. */
export function setSegmentOpeningBase(room: FloorPlanRoom, ref: LineEdgeRef, si: number, base: number): FloorPlanRoom {
  return updateHostSegment(room, ref, si, seg => {
    const b = Math.max(0, roundToHalfInch(base));
    return b === seg.openingBase ? seg : { ...seg, openingBase: b };
  });
}

/**
 * Append a segment to a host. Materializes a missing sub-wall container;
 * returns the room unchanged if the host's parent doesn't exist (e.g. a
 * chamfer diagonal with no chamfer).
 */
export function addSegment(room: FloorPlanRoom, ref: LineEdgeRef, seg: FloorPlanEdgeSegment): FloorPlanRoom {
  return updateHostSegments(room, ref, segments => [...segments, seg]);
}

/**
 * Add segments to a host, leaving its whole list in position order. Like
 * `addSegment`, it materializes a missing host list, even for no segments,
 * and returns the room unchanged if the host's parent doesn't exist.
 */
export const addSegmentsInPositionOrder = (room: FloorPlanRoom, ref: LineEdgeRef, added: readonly FloorPlanEdgeSegment[]): FloorPlanRoom =>
  updateHostSegments(room, ref, segments => [...segments, ...added].sort((first, second) => first.position - second.position));

/** Remove the segment at `si` from a host. */
export function removeSegment(room: FloorPlanRoom, ref: LineEdgeRef, si: number): FloorPlanRoom {
  return updateHostSegments(room, ref, segments => si >= 0 && si < segments.length ? segments.filter((_, i) => i !== si) : segments);
}
